// context/BoardContext.tsx
"use client";

import { postWithAuth, getWithAuth } from "@/lib/api-helpers";
import { saveStoredBoardViewId } from "@/lib/boardViewStorage";
import type { BoardProperties, BoardProperty, Comment, DatabaseSource, LayoutSettings, SortItem, ViewCollection,} from "@/types/board";
import type { IChartSettings } from "@/models/types/ViewTypes";
import { type ReactNode, createContext, useContext, useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useSocketContext } from "@/contexts/socketContext";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { useBlockActions } from "@/contexts/blockContext";
import type { Block } from "@/types/block";
import { loadDataSourcesMutation, upsertBlocksMutation, reorderSchemaMutation, appDB, toBlock, getPendingBlockIds } from "@/indexedDB";
import {
  isDatasourceDead,
  DATASOURCE_CASCADE_EVENT,
  type DatasourceCascadeDetail,
} from "@/lib/datasourceTombstones";
import { eventBus } from "@/services-frontend/comment/eventBus";
import { useViewScope } from "@/contexts/viewScopeContext";

interface BoardContextType {
  // removeBoard: (blockId: string) => void; // Commented out

  dataSources: Record<string, DatabaseSource>; // key: dataSourceId -> DatabaseSource
  setNotesState: (dataSourceId: string, notes: Block[]) => void;
  updateAllNotes: (dataSourceId: string, newNotes: Block[]) => void;
  updateNoteComments: (dataSourceId: string, noteId: string, newComment: Comment) => void;
  refreshNotes: (dataSourceId: string) => Promise<void>; // Refresh notes for a dataSourceId from API
  getNotesByDataSourceId: (dataSourceId: string) => Block[]; // Helper to get notes for a specific dataSourceId
  getNoteById: (noteId: string) => Block | null; // Helper to get a block by ID from global block context
  getRelationNoteTitle: (noteId: string, linkedDatabaseId: string, fallbackTitle?: string) => string; // Get current title of a relation note
  getValidRelationIds: (noteIds: string[], linkedDatabaseId: string) => string[]; // Filters out deleted IDs without hiding unloaded ones

  // Data Source management
  setDataSource: (dataSourceId: string, dataSource: DatabaseSource) => void;
  setDataSources: (dataSources: Record<string, DatabaseSource>) => void;
  updateDataSource: (dataSourceId: string, updatedDataSource: Partial<DatabaseSource>) => void;
  patchDataSourceProperty: (dataSourceId: string, propertyId: string, patch: Partial<Record<string, any>>) => void;
  getDataSource: (dataSourceId: string) => DatabaseSource | undefined;

  // Current state
  // Note: boardId refers to the collection_view block ID (the board's block ID)
  currentDataSource: Record<string, string | undefined>; // boardId -> dataSourceId
  getCurrentDataSource: (boardId: string) => DatabaseSource | undefined;
  getCurrentDataSourceProperties: (boardId: string) => BoardProperties;
  setCurrentDataSource: (boardId: string, dataSourceId: string | undefined) => void;

  groupBy: Record<string, string | undefined>; // key: viewTypeId -> propertyId
  setGroupBy: (viewTypeId: string, propertyId: string | undefined) => void;
  getGroupBy: (boardId: string) => string | undefined; // Helper to get groupBy for current view

  filters: Record<string, Record<string, string[]>>; // key: viewTypeId -> { propertyId: string[] }
  setBoardFilters: (viewTypeId: string, boardFilters: Record<string, string[]>) => void;
  getFilters: (boardId: string, explicitViewTypeId?: string) => Record<string, string[]>; // Helper to get filters for current view

  advancedFilters: Record<string, any[]>; // key: viewTypeId -> IAdvancedFilterGroup[]
  setAdvancedFilters: (viewTypeId: string, advancedFilters: any[]) => void;
  getAdvancedFilters: (boardId: string, explicitViewTypeId?: string) => any[]; // Helper to get advanced filters for current view

  sortBy: Record<string, SortItem[]>; // key: viewTypeId -> SortItem[]
  setBoardSortBy: (viewTypeId: string, boardSorts: SortItem[]) => void;
  getSortBy: (boardId: string) => SortItem[]; // Helper to get sortBy for current view

  chartSettings: Record<string, IChartSettings | undefined>; // key: viewTypeId -> IChartSettings
  setChartSettings: (viewTypeId: string, chartSettings: IChartSettings | undefined) => void;
  getChartSettings: (boardId: string) => IChartSettings | undefined; // Helper to get chartSettings for current view

  propertyVisibility: Record<string, string[]>; // key: viewTypeId -> propertyId[]
  setPropertyVisibility: (viewTypeId: string, propertyIds: string[]) => void;
  getPropertyVisibility: (boardId: string) => string[]; // Helper to get propertyVisibility for current view

  layoutSettings: Record<string, LayoutSettings | undefined>; // key: viewTypeId -> LayoutSettings
  setLayoutSettings: (viewTypeId: string, layoutSettings: LayoutSettings | undefined) => void;
  getLayoutSettings: (boardId: string) => LayoutSettings | undefined; // Helper to get layoutSettings for current view

  propertyOrder: Record<string, string[]>; // key: boardId → order of properties
  setPropertyOrder: (boardId: string, order: string[]) => void;

  currentBoardNoteId: string | null;
  setCurrentBoardNoteId: (noteId: string | null) => void;

  currentView: Record<string, { id?: string; type: string }>; // boardId -> { id?, type }
  setCurrentView: (boardId: string, viewId: string, viewType: string) => void;

  searchQuery: Record<string, string>;
  setSearchQuery: (boardId: string, query: string) => void;

  isHistoryMode: boolean;
  setIsHistoryMode: (isHistory: boolean) => void;
}

const BoardContext = createContext<BoardContextType | undefined>(undefined);

export const BoardProvider = ({ children }: { children: ReactNode }) => {
  const { socket } = useSocketContext();
  const { getBlock, upsertBlocks } = useBlockActions();
  const { currentWorkspace } = useWorkspaceContext();
  const [dataSources, setDataSourcesState] = useState<Record<string, DatabaseSource>>({});
  const [currentDataSource, setCurrentDataSourceState] = useState<Record<string, string | undefined>>({});
  const [groupBy, setGroupByState] = useState<Record<string, string | undefined>>({});
  const [filters, setFilters] = useState<Record<string, Record<string, string[]>>>({});
  const [advancedFilters, setAdvancedFiltersState] = useState<Record<string, any[]>>({});
  const [propertyOrder, setPropertyOrderState] = useState<Record<string, string[]>>({});
  const [propertyVisibility, setPropertyVisibilityState] = useState<Record<string, string[]>>({});
  const [currentBoardNoteId, setCurrentBoardNoteId] = useState<string | null>(null);
  const [currentView, setCurrentViewState] = useState<Record<string, { id?: string; type: string }>>({});
  const [sortBy, setSortBy] = useState<Record<string, SortItem[]>>({});
  const [chartSettings, setChartSettingsState] = useState<Record<string, IChartSettings | undefined>>({});
  const [layoutSettings, setLayoutSettingsState] = useState<Record<string, LayoutSettings | undefined>>({});
  const [searchQuery, setSearchQueryState] = useState<Record<string, string>>({});
  const [isHistoryMode, setIsHistoryMode] = useState<boolean>(false);

  // Refs for stable getters (prevents infinite re-render loops in components)
  const dataSourcesRef = useRef(dataSources);
  const currentDataSourceRef = useRef(currentDataSource);
  const groupByRef = useRef(groupBy);
  const filtersRef = useRef(filters);
  const advancedFiltersRef = useRef(advancedFilters);
  const propertyVisibilityRef = useRef(propertyVisibility);
  const currentViewRef = useRef(currentView);
  const sortByRef = useRef(sortBy);
  const chartSettingsRef = useRef(chartSettings);
  const layoutSettingsRef = useRef(layoutSettings);

  // Sync refs with state immediately on EVERY render
  dataSourcesRef.current = dataSources;
  currentDataSourceRef.current = currentDataSource;
  groupByRef.current = groupBy;
  filtersRef.current = filters;
  advancedFiltersRef.current = advancedFilters;
  propertyVisibilityRef.current = propertyVisibility;
  currentViewRef.current = currentView;
  sortByRef.current = sortBy;
  chartSettingsRef.current = chartSettings;
  layoutSettingsRef.current = layoutSettings;

  // Data Source management functions (Defined early to avoid used-before-declaration errors)
  const setDataSource = useCallback((dataSourceId: string, dataSource: DatabaseSource) => {
    setDataSourcesState((prev) => ({
      ...prev,
      [dataSourceId]: dataSource,
    }));
  }, []);

  const setDataSources = useCallback((newDataSources: Record<string, DatabaseSource>) => {
    setDataSourcesState(newDataSources);
  }, []);

  const updateDataSource = useCallback((dataSourceId: string, updatedDataSource: Partial<DatabaseSource>) => {
    setDataSourcesState((prev) => {
      const existing = prev[dataSourceId];
      if (!existing) return prev;
      return {
        ...prev,
        [dataSourceId]: { ...existing, ...updatedDataSource } as DatabaseSource,
      };
    });
  }, []);

  const patchDataSourceProperty = useCallback((dataSourceId: string, propertyId: string, patch: Partial<Record<string, any>>) => {
    setDataSourcesState((prev) => {
      const existing = prev[dataSourceId];
      if (!existing?.properties?.[propertyId]) return prev;
      return {
        ...prev,
        [dataSourceId]: {
          ...existing,
          properties: {
            ...existing.properties,
            [propertyId]: { ...existing.properties[propertyId], ...patch } as BoardProperty,
          },
        },
      };
    });
  }, []);

  const setNotesState = useCallback((dataSourceId: string, boardNotes: Block[]) => {
    const normalizedDataSourceId = typeof dataSourceId === "string" ? dataSourceId : String(dataSourceId);
    // setNotes((prev) => ({
    //   ...prev,
    //   [normalizedDataSourceId]: boardNotes,
    // }));
  }, []);

  const updateAllNotes = useCallback((dataSourceId: string, newNotes: Block[]) => {
    const normalizedDataSourceId = typeof dataSourceId === "string" ? dataSourceId : String(dataSourceId);
    // setNotes((prev) => ({
    //   ...prev,
    //   [normalizedDataSourceId]: newNotes,
    // }));
  }, []);

  const updateNoteComments = useCallback((dataSourceId: string, noteId: string, newComment: Comment) => {
    const normalizedDataSourceId = typeof dataSourceId === "string" ? dataSourceId : String(dataSourceId);
    // setNotes((prev) => {
    //   const dataSourceNotes = prev[normalizedDataSourceId] || [];
    //   const updatedNotes = dataSourceNotes.map((note) =>
    //     note._id === noteId ? { ...note, comments: [...(note.comments || []), newComment] } : note,
    //   );
    //   return { ...prev, [normalizedDataSourceId]: updatedNotes };
    // });
  }, []);

  // Refresh blocks for a dataSourceId by fetching from API
  const refreshNotes = useCallback(async (dataSourceId: string) => {
    const normalizedDataSourceId = typeof dataSourceId === "string" ? dataSourceId : String(dataSourceId);
    try {
      const response = await getWithAuth(`/api/database/get-data-source/${normalizedDataSourceId}`) as {
        success?: boolean;
        collection?: {
          dataSource?: DatabaseSource;
          blocks?: Block[];
        };
      };

      if (response?.success && response.collection) {
        // Store blocks in global block context
        if (response.collection.blocks && response.collection.blocks.length > 0) {
          const serverBlocks = response.collection.blocks;
          // Protect rows with unsynced local edits: a refetch must never
          // overwrite a block whose edit hasn't reached the server yet, or it
          // silently reverts the user's change (in context AND IDB). Mirrors
          // the guard peekContext / advanced-editor apply on their own fetches.
          let safeBlocks = serverBlocks;
          let pendingIds = new Set<string>();
          try {
            pendingIds = await getPendingBlockIds();
            if (pendingIds.size > 0) {
              const existing = await appDB.blocks.bulkGet(serverBlocks.map((b) => b._id));
              const overrides = new Map<string, Block>();
              for (const r of existing) {
                if (r && pendingIds.has(r._id)) overrides.set(r._id, toBlock(r));
              }
              if (overrides.size > 0) {
                safeBlocks = serverBlocks.map((b) => overrides.get(b._id) ?? b);
              }
            }
          } catch (err) {
            console.warn("[BoardContext] pending-edit guard failed; using raw server blocks:", err);
          }
          upsertBlocks(safeBlocks);
          // Skip pending rows in the IDB write so their unsynced local record
          // (isSync: 0) is left intact for the sync queue to push.
          const idbBlocks = safeBlocks.filter((b) => !pendingIds.has(b._id));
          if (idbBlocks.length > 0) {
            upsertBlocksMutation(idbBlocks).catch((err) => {
              console.error("[BoardContext] Failed to upsert blocks to IDB:", err);
            });
          }
        }

        // Also update dataSource if provided
        if (response.collection.dataSource) {
          const ds = response.collection.dataSource;
          const dsId = ds._id || normalizedDataSourceId;
          setDataSource(dsId, ds);
          // Also write to IDB so datasource is available offline
          const wsId = ds.workspaceId || "";
          loadDataSourcesMutation([ds], wsId).catch((err) => {
            console.error("[BoardContext] Failed to load datasource to IDB:", err);
          });
        }
      }
    } catch (error) {
      console.error(`Failed to refresh blocks for dataSourceId ${normalizedDataSourceId}:`, error);
    }
  }, [upsertBlocks, setDataSource]);

  // Drop datasources the delete cascade removed — context is what the UI
  // renders from, so a stale entry keeps a deleted database fully alive.
  useEffect(() => {
    const handleCascade = (event: Event) => {
      const detail = (event as CustomEvent<DatasourceCascadeDetail>).detail;
      const ids = detail?.dataSourceIds ?? [];
      if (ids.length === 0) return;
      setDataSourcesState((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const id of ids) {
          if (next[id]) {
            delete next[id];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };
    window.addEventListener(DATASOURCE_CASCADE_EVENT, handleCascade);
    return () => window.removeEventListener(DATASOURCE_CASCADE_EVENT, handleCascade);
  }, []);

  // Listen for note update events from webhooks
  useEffect(() => {
    if (!socket) return;

    const handleNoteUpdated = (data: { noteId: string; dataSourceId: string }) => {
      if (data.dataSourceId) {
        // Refresh notes for this dataSourceId
        refreshNotes(data.dataSourceId).catch((err) => {
          console.error("[Board Context] Failed to refresh notes after update:", err);
        });
      }
    };

    socket.on("note-updated", handleNoteUpdated);

    return () => {
      socket.off("note-updated", handleNoteUpdated);
    };
  }, [socket]);

  // Listen for datasource invalidations broadcast by socketEventRouter.
  // Fires for DATASOURCE_PAGE_ADDED (new row) and DATASOURCE_UPDATED
  // (any view/property/setting change, board creation). Receiver resolves
  // the affected dataSource id and refreshes its rows + definition.
  useEffect(() => {
    const handler = (data: { dataSourceId?: string; blockId?: string }) => {
      console.log("[BoardContext] 🔔 datasource:invalidate received", data);

      // View SETTINGS (property visibility, filters, sorts, grouping, layout)
      // live on the collection_view block itself, at
      // value.viewsTypes[].settings — NOT on the datasource. refreshNotes()
      // below fetches /get-data-source, which returns the datasource plus its
      // ROW blocks and never the collection_view block, so a settings change
      // by another user refreshed their rows but left the view config stale
      // and nothing visibly changed. Refetch that block too.
      if (data?.blockId) {
        const viewBlockId = String(data.blockId);
        postWithAuth<{ success?: boolean; blocks?: any[] }>(
          "/api/blocks/get-many",
          { blockIds: [viewBlockId] },
        )
          .then((res) => {
            const blocks = (res as any)?.blocks;
            if (!Array.isArray(blocks) || blocks.length === 0) return;
            upsertBlocks(blocks);
            upsertBlocksMutation(blocks).catch((err) =>
              console.error("[BoardContext] view-block IDB persist failed:", err),
            );
          })
          .catch((err) =>
            console.error("[BoardContext] view-settings refetch failed:", err),
          );
      }
      // 1. Direct dataSourceId from the payload (preferred).
      let targetId = data?.dataSourceId ? String(data.dataSourceId) : "";

      // 2. Fall back to looking up via blockId — many view-setting endpoints
      //    only have the collection_view block id at hand. The current
      //    dataSource map (boardId → dataSourceId) bridges the gap.
      if (!targetId && data?.blockId) {
        const fromMap = currentDataSourceRef.current?.[String(data.blockId)];
        if (fromMap) targetId = String(fromMap);
      }

      // 3. NEW board case: a collection_view block has just been delivered
      //    via Yjs but BoardContainer hasn't yet wired its boardId →
      //    dataSourceId mapping. Pull the dataSourceId straight off the
      //    block's own value.viewsTypes[].databaseSourceId so the very
      //    first DATASOURCE_UPDATED ping after creation can find it.
      if (!targetId && data?.blockId) {
        try {
          const block: any = getBlock(String(data.blockId));
          // Skip widgets (their datasource may differ); fall back to [0] defensively.
          const viewsTypes = block?.value?.viewsTypes;
          const primaryView = Array.isArray(viewsTypes)
            ? (viewsTypes.find((v: any) => !v?.pinnedToDashboard) ?? viewsTypes[0])
            : undefined;
          const fromBlock =
            primaryView?.databaseSourceId ||
            block?.value?.dataSourceId ||
            block?.value?.collectionId;
          if (fromBlock) targetId = String(fromBlock);
        } catch {
          // ignore — fall through to the next strategy
        }
      }

      // 4. Last resort: blockId IS the dataSource id (some legacy paths).
      if (!targetId && data?.blockId && dataSourcesRef.current?.[String(data.blockId)]) {
        targetId = String(data.blockId);
      }

      if (!targetId) {
        console.warn(
          "[BoardContext] datasource:invalidate could not resolve dataSourceId — skipping",
          data,
        );
        return;
      }

      console.log(`[BoardContext] 🔄 calling refreshNotes(${targetId})`);
      refreshNotes(targetId)
        .then(() => console.log(`[BoardContext] ✅ refreshNotes(${targetId}) finished`))
        .catch((err) =>
          console.error("[BoardContext] datasource:invalidate refresh failed:", err),
        );
    };
    eventBus.on("datasource:invalidate", handler);
    return () => {
      eventBus.off("datasource:invalidate", handler);
    };
  }, [refreshNotes, getBlock, upsertBlocks]);

  // Row-canonical block sync (BLOCK_UPDATED → blocks:invalidate). A button
  // block renders from its IndexedDB row via useLiveBlock — doc attrs are a
  // cold snapshot — so a peer's rename reaches this client ONLY through this
  // refetch; the CRDT never carries it. Fetch the named blocks and upsert
  // both stores; the Dexie live query re-renders every mounted button.
  //
  // Generic on purpose: the API decides WHICH block types ping (its
  // ROW_CANONICAL_BLOCK_TYPES list in note.controller batch-update); this
  // listener just fetches whatever ids arrive, so a new row-canonical block
  // type needs no frontend change. It lives here rather than blockContext
  // because that context deliberately never imports the Dexie client, while
  // this file's view-block refetch above already proved this exact
  // get-many + upsertBlocks + upsertBlocksMutation combination cycle-free.
  useEffect(() => {
    const handler = (data: { blockIds?: string[] }) => {
      const ids = Array.from(
        new Set((data?.blockIds ?? []).map(String).filter(Boolean)),
      );
      if (ids.length === 0) return;
      postWithAuth<{ success?: boolean; blocks?: any[] }>(
        "/api/blocks/get-many",
        { blockIds: ids },
      )
        .then((res) => {
          const blocks = (res as any)?.blocks;
          if (!Array.isArray(blocks) || blocks.length === 0) return;
          upsertBlocks(blocks);
          upsertBlocksMutation(blocks).catch((err) =>
            console.error("[BoardContext] block refetch IDB persist failed:", err),
          );
        })
        .catch((err) =>
          console.error("[BoardContext] blocks:invalidate refetch failed:", err),
        );
    };
    eventBus.on("blocks:invalidate", handler);
    return () => {
      eventBus.off("blocks:invalidate", handler);
    };
  }, [upsertBlocks]);

  const workspaceIdRef = useRef("");
  workspaceIdRef.current = currentWorkspace?._id || "";

  const savePropertyOrder = useCallback(async (boardId: string, order: string[], dataSourceId: string) => {
    // Get current view for audit purposes
    // Note: boardId is the collection_view block ID
    const currentViewData = currentViewRef.current[boardId];
    // REMOVED: Get board from boards array - boards are now in global block context
    // Components should get viewId from global block context if needed
    let viewId: string | undefined = currentViewData?.id;

    await reorderSchemaMutation({
      dataSourceId,
      blockId: boardId, // Required for permission checking
      viewId, // Optional for audit
      order,
      workspaceId: workspaceIdRef.current,
    });
  }, []);

  const propertyOrderRef = useRef(propertyOrder);
  propertyOrderRef.current = propertyOrder;

  const setPropertyOrder = useCallback((boardId: string, order: string[]) => {
    // Note: boardId is the collection_view block ID
    const filteredOrder = order.filter((id) => id !== "title");

    // Central guard: block reorder when the datasource is locked.
    // boardId is a collection_view block id, or a datasource id (widget).
    const lockDataSourceId = currentDataSourceRef.current[boardId] || boardId;
    if (dataSourcesRef.current[lockDataSourceId]?.isLocked === true) {
      return;
    }

    // One session bucket per DATASOURCE — the server stores one order per
    // datasource (re-order-schema reorders the schema itself), so every
    // surface showing that datasource shares the same key.
    const mappedDsId = currentDataSourceRef.current[boardId];
    const dataSourceId = mappedDsId || (dataSourcesRef.current[boardId] ? boardId : undefined);
    const stateKey = dataSourceId ?? boardId;

    setPropertyOrderState((prev) => ({
      ...prev,
      [stateKey]: filteredOrder,
    }));

    if (dataSourceId) {
      // blockId is only the API's permission anchor — the owning board block.
      const blockIdForSave = mappedDsId
        ? boardId
        : Object.entries(currentDataSourceRef.current).find(([, dsId]) => dsId === dataSourceId)?.[0] ??
          String((dataSourcesRef.current[dataSourceId] as any)?.mainView ?? boardId);
      const previousOrder = propertyOrderRef.current[stateKey];
      savePropertyOrder(blockIdForSave, filteredOrder, dataSourceId).catch((error) => {
        console.error("Failed to save property order:", error);
        if (previousOrder) {
          setPropertyOrderState((prev) => ({
            ...prev,
            [stateKey]: previousOrder,
          }));
        }
      });
    } else {
      console.warn("No dataSourceId found for board:", boardId);
    }
  }, [savePropertyOrder]);
  const getCurrentViewTypeId = useCallback((boardId: string): string | null => {
    // Note: boardId is the collection_view block ID
    const currentViewData = currentViewRef.current[boardId];
    if (currentViewData?.id) {
      return typeof currentViewData.id === "string" ? currentViewData.id : String(currentViewData.id);
    }
    return null;
  }, []);

  const setBoardFilters = useCallback((viewTypeId: string, boardFilters: Record<string, string[]>) => {
    setFilters((prev) => ({ ...prev, [viewTypeId]: boardFilters }));
  }, []);

  const setAdvancedFilters = useCallback((viewTypeId: string, advancedFiltersArray: any[]) => {
    setAdvancedFiltersState((prev) => ({ ...prev, [viewTypeId]: advancedFiltersArray }));
  }, []);

  const setGroupBy = useCallback((viewTypeId: string, propertyId: string | undefined) => {
    setGroupByState((prev) => ({
      ...prev,
      [viewTypeId]: propertyId,
    }));
  }, []);

  const setBoardSortBy = useCallback((viewTypeId: string, sorts: SortItem[]) => {
    setSortBy((prev) => ({
      ...prev,
      [viewTypeId]: sorts,
    }));
  }, []);

  const setPropertyVisibility = useCallback((viewTypeId: string, propertyIds: string[]) => {
    setPropertyVisibilityState((prev) => {
      const updated = {
        ...prev,
        [viewTypeId]: propertyIds,
      };
      return updated;
    });
  }, []);

  const getBoardProperty = useCallback(
    (boardId: string, propertyId?: string): BoardProperty | undefined => {
      if (!propertyId) return undefined;
      const dataSourceId = currentDataSourceRef.current[boardId];
      return dataSourceId
        ? dataSourcesRef.current[dataSourceId]?.properties?.[propertyId]
        : undefined;
    },
    [],
  );

  // A deleted option matches nothing and renders as a raw id. Drop those
  // values; a filter left with none is dropped entirely.
  const OPTION_TYPES = ["status", "select", "multi_select", "multiselect", "priority"];
  const pruneMissingOptions = useCallback(
    (boardId: string, propertyId: string, values: string[]): string[] => {
      const property = getBoardProperty(boardId, propertyId);
      if (!property || !Array.isArray(values) || values.length === 0) return values;

      // Pruned like deleted options: a deleted row or datasource drops the value.
      if (property.type === "relation") {
        const linkedId = property.linkedDatabaseId ? String(property.linkedDatabaseId) : "";
        if (!linkedId) return values;
        if (isDatasourceDead(linkedId)) return [];
        const rowIds = dataSourcesRef.current[linkedId]?.blockIds;
        if (!Array.isArray(rowIds)) return values;
        const known = new Set(rowIds.map(String));
        const kept = values.filter((value) => known.has(String(value)));
        // Identity-preserving — callers use `pruned !== values` as the changed signal.
        return kept.length === values.length ? values : kept;
      }

      if (!OPTION_TYPES.includes(property.type)) {
        // Option ids stranded on a non-option property (its type changed) —
        // they'd otherwise be applied literally, e.g. as a text search string.
        const kept = values.filter((value) => !String(value).startsWith("opt_"));
        return kept.length === values.length ? values : kept;
      }
      // Group ids are live references too — a status filter may name a group,
      // which stands for whatever it holds now.
      const known = new Set(
        [
          ...(property.options ?? []).map((option: any) => option?.id),
          ...((property as any).groups ?? []).map((group: any) => group?.id),
        ]
          .filter(Boolean)
          .map(String),
      );
      // No options defined yet (schema not loaded) — leave the filter alone.
      if (known.size === 0) return values;
      const kept = values.filter((value) => known.has(String(value)));
      return kept.length === values.length ? values : kept;
    },
    [getBoardProperty],
  );

  const getFilters = useCallback((boardId: string, explicitViewTypeId?: string): Record<string, string[]> => {
    // Note: boardId is the collection_view block ID
    const viewTypeId = explicitViewTypeId ?? getCurrentViewTypeId(boardId);
    if (!viewTypeId) return {};
    const stored = filtersRef.current[viewTypeId] || {};
    let changed = false;
    const live: Array<[string, string[]]> = [];
    for (const [propId, values] of Object.entries(stored)) {
      const pruned = pruneMissingOptions(boardId, propId, values);
      if (pruned !== values) changed = true;
      // Emptied entries stay: the filter UI keeps the property chip with a
      // blank value picker; appliers treat an empty value list as a no-op.
      live.push([propId, pruned]);
    }
    return changed ? Object.fromEntries(live) : stored;
  }, [getCurrentViewTypeId, pruneMissingOptions]);

  const getAdvancedFilters = useCallback((boardId: string, explicitViewTypeId?: string): any[] => {
    // Note: boardId is the collection_view block ID
    const viewTypeId = explicitViewTypeId ?? getCurrentViewTypeId(boardId);
    if (!viewTypeId) return [];
    const stored = advancedFiltersRef.current[viewTypeId] || [];
    const ruleIsLive = (rule: any): boolean => {
      // Nested rules (e.g. Sprint → Status) hold a value belonging to a
      // property of the LINKED datasource, so validating it against
      // rule.propertyId would always fail.
      if (rule?.nestedPropertyId) return true;
      // Operators like "is empty" carry no value — nothing to validate.
      const raw = rule?.value;
      if (raw === undefined || raw === null || raw === "") return true;
      const values = (Array.isArray(raw) ? raw : [raw]).map(String);
      return pruneMissingOptions(boardId, rule.propertyId, values).length > 0;
    };
    // Identity-preserving — unchanged input returns the same reference so
    // render memos keyed on the result stay stable.
    const prune = (nodes: any[]): any[] => {
      let changed = false;
      const out: any[] = [];
      for (const node of nodes) {
        if (Array.isArray(node?.rules)) {
          const rules = prune(node.rules);
          if (rules.length === 0) {
            changed = true;
          } else if (rules !== node.rules) {
            changed = true;
            out.push({ ...node, rules });
          } else {
            out.push(node);
          }
        } else if (ruleIsLive(node)) {
          out.push(node);
        } else {
          changed = true;
        }
      }
      return changed ? out : nodes;
    };
    return prune(stored);
  }, [getCurrentViewTypeId, pruneMissingOptions]);

  const getGroupBy = useCallback((boardId: string): string | undefined => {
    // Note: boardId is the collection_view block ID
    const viewTypeId = getCurrentViewTypeId(boardId);
    if (!viewTypeId) return undefined;
    return groupByRef.current[viewTypeId];
  }, [getCurrentViewTypeId]);

  const getSortBy = useCallback((boardId: string): SortItem[] => {
    // Note: boardId is the collection_view block ID
    const viewTypeId = getCurrentViewTypeId(boardId);
    if (!viewTypeId) return [];
    return sortByRef.current[viewTypeId] || [];
  }, [getCurrentViewTypeId]);

  const setChartSettings = useCallback((viewTypeId: string, chartSettings: IChartSettings | undefined) => {
    setChartSettingsState((prev) => ({
      ...prev,
      [viewTypeId]: chartSettings,
    }));
  }, []);

  const getChartSettings = useCallback((boardId: string): IChartSettings | undefined => {
    // Note: boardId is the collection_view block ID
    const viewTypeId = getCurrentViewTypeId(boardId);
    if (!viewTypeId) return undefined;
    return chartSettingsRef.current[viewTypeId];
  }, [getCurrentViewTypeId]);

  const setLayoutSettings = useCallback((viewTypeId: string, layoutSettings: LayoutSettings | undefined) => {
    setLayoutSettingsState((prev) => ({
      ...prev,
      [viewTypeId]: layoutSettings,
    }));
  }, []);

  const getLayoutSettings = useCallback((boardId: string): LayoutSettings | undefined => {
    // Note: boardId is the collection_view block ID
    const viewTypeId = getCurrentViewTypeId(boardId);
    if (!viewTypeId) return undefined;
    return layoutSettingsRef.current[viewTypeId];
  }, [getCurrentViewTypeId]);

  const getPropertyVisibility = useCallback((boardId: string): string[] => {
    // Note: boardId is the collection_view block ID
    const viewTypeId = getCurrentViewTypeId(boardId);
    if (!viewTypeId) {
      return [];
    }
    return propertyVisibilityRef.current[viewTypeId] || [];
  }, [getCurrentViewTypeId]);

  const setSearchQuery = useCallback((boardId: string, query: string) => {
    // Note: boardId is the collection_view block ID
    setSearchQueryState((prev) => ({
      ...prev,
      [boardId]: query,
    }));
  }, []);



  const setCurrentView = useCallback((boardId: string, viewId: string, viewType: string) => {
    // Note: boardId is the collection_view block ID
    saveStoredBoardViewId(currentWorkspace?._id, boardId, viewId);
    // Prevent infinite loops by checking if the value is already set
    setCurrentViewState((prev) => {
      const current = prev[boardId];
      // If the same view is already set, don't update
      if (current?.id === viewId && current?.type === viewType) {
        return prev;
      }
      return {
        ...prev,
        [boardId]: { id: viewId || undefined, type: viewType },
      };
    });

    // Sync currentDataSource from global block context
    const boardBlock = getBlock(boardId);
    if (boardBlock && boardBlock.blockType === "collection_view") {
      let view;
      if (viewId) {
        view = boardBlock.value.viewsTypes?.find((v) => v._id === viewId);
      } else {
        view = boardBlock.value.viewsTypes?.find((v) => v.viewType === viewType);
      }

      if (view?.databaseSourceId) {
        const dataSourceId = typeof view.databaseSourceId === "string"
          ? view.databaseSourceId
          : String(view.databaseSourceId);

        setCurrentDataSourceState((prev) => {
          if (prev[boardId] !== dataSourceId) {
            return {
              ...prev,
              [boardId]: dataSourceId,
            };
          }
          return prev;
        });
      }
    }
  }, [currentWorkspace?._id, getBlock]);

  // Handlers moved up

  const getDataSource = useCallback((dataSourceId: string): DatabaseSource | undefined => {
    return dataSourcesRef.current[dataSourceId];
  }, []);

  const getCurrentDataSource = useCallback((boardId: string): DatabaseSource | undefined => {
    // Note: boardId is the collection_view block ID
    const dataSourceId = currentDataSourceRef.current[boardId];
    if (!dataSourceId) return undefined;
    return dataSourcesRef.current[dataSourceId];
  }, []);

  const getCurrentDataSourceProperties = useCallback((boardId: string): BoardProperties => {
    // Note: boardId is the collection_view block ID
    const dataSource = getCurrentDataSource(boardId);
    return dataSource?.properties || {};
  }, [getCurrentDataSource]);

  const setCurrentDataSource = useCallback((boardId: string, dataSourceId: string | undefined) => {
    // Note: boardId is the collection_view block ID
    setCurrentDataSourceState((prev) => {
      // Only update if the value actually changed
      if (prev[boardId] === dataSourceId) {
        return prev;
      }
      return {
        ...prev,
        [boardId]: dataSourceId,
      };
    });
  }, []);

  // Sync currentDataSource when currentView changes (using global block context)
  useEffect(() => {
    // Get all boardIds from currentView
    const boardIds = Object.keys(currentView);

    boardIds.forEach((boardId) => {
      const currentViewData = currentView[boardId];
      if (!currentViewData) return;

      // Get board block from global block context
      const boardBlock = getBlock(boardId);
      if (!boardBlock || boardBlock.blockType !== "collection_view") return;

      let view;

      if (currentViewData.id) {
        const viewIdStr = typeof currentViewData.id === "string"
          ? currentViewData.id
          : String(currentViewData.id);
        view = boardBlock.value.viewsTypes?.find((v) => v._id === currentViewData.id);
      } else if (currentViewData.type) {
        view = boardBlock.value.viewsTypes?.find((v) => v.viewType === currentViewData.type);
      }

      if (view?.databaseSourceId) {
        const dataSourceId = view.databaseSourceId
        setCurrentDataSourceState((prev) => {
          if (prev[boardId] !== dataSourceId) {
            return {
              ...prev,
              [boardId]: dataSourceId,
            };
          }
          return prev;
        });
      }
    });
  }, [currentView, getBlock, getDataSource, setDataSource]);



  // REMOVED: Load all data sources for loaded boards' views on mount/updates
  // Datasources are now fetched on-demand when needed by components
  // OLD CODE (commented out):
  // const fetchingDataSourcesRef = useRef<Set<string>>(new Set());
  // useEffect(() => {
  //   const fetchMissingDataSources = async () => {
  //     const missingIds = new Set<string>();
  //     boards.forEach((board) => {
  //       (board.viewsType || []).forEach((vt) => {
  //         const dsId = vt.databaseSourceId;
  //         if (dsId) {
  //           const normalizedId = typeof dsId === "string" ? dsId : String(dsId);
  //           if (!dataSources[normalizedId] && !fetchingDataSourcesRef.current.has(normalizedId)) {
  //             missingIds.add(normalizedId);
  //           }
  //         }
  //       });
  //     });
  //     if (missingIds.size === 0) return;
  //     missingIds.forEach((id) => fetchingDataSourcesRef.current.add(id));
  //     await Promise.all(
  //       Array.from(missingIds).map(async (id) => {
  //         try {
  //           const res = (await getWithAuth(`/api/database/getdataSource/${id}`)) as { success?: boolean; collection?: { dataSource?: any } };
  //           if (res?.success && res.collection?.dataSource) {
  //             const ds = res.collection.dataSource;
  //             const normalizedId = typeof ds._id === "string" ? ds._id : ds._id?.toString?.() || id;
  //             setDataSource(normalizedId, ds as any);
  //           }
  //         } catch (e) {
  //           // ignore individual failures
  //         } finally {
  //           fetchingDataSourcesRef.current.delete(id);
  //         }
  //       }),
  //     );
  //   };
  //   fetchMissingDataSources();
  // }, [boards, dataSources]);


  // Helpers moved up

  // Helper to get blocks for a specific dataSourceId
  // Gets blocks from block context using datasource's blockIds array
  const getNotesByDataSourceId = useCallback((dataSourceId: string): Block[] => {
    const normalizedDataSourceId = typeof dataSourceId === "string" ? dataSourceId : String(dataSourceId);

    // Get datasource to access its blockIds array
    const dataSource = dataSourcesRef.current[normalizedDataSourceId];
    if (!dataSource || !dataSource.blockIds || dataSource.blockIds.length === 0) {
      return [];
    }

    // Skip soft-deleted rows: a deleted id can linger in blockIds until the
    // delete syncs and the datasource refetches, which would resurface the row.
    const blocks: Block[] = [];
    dataSource.blockIds.forEach((blockId) => {
      const block = getBlock(blockId);
      if (block && block.status !== "deleted") {
        blocks.push(block);
      }
    });

    return blocks as Block[];
  }, [getBlock]);

  // Helper to get a block by ID from global block context
  // This is useful for relation properties where we need to find the current title
  const getNoteById = (noteId: string): Block | null => {
    if (!noteId) return null;


    // Get block from global block context
    const block = getBlock(noteId);
    return block || null;
  };

  // Helper to get relation block title from global block context (with fallback to stored value)
  const getRelationNoteTitle = (noteId: string, linkedDatabaseId: string, fallbackTitle?: string): string => {
    if (!noteId) return fallbackTitle || "New page";

    // Get block from global block context
    const block = getBlock(noteId);
    if (block?.value?.title) {
      return block.value.title;
    }

    // Fallback to stored title or default
    return fallbackTitle || "New page";
  };

  // Helper to filter out deleted IDs without prematurely hiding them if the target DB isn't fetched
  const getValidRelationIds = (noteIds: string[], linkedDatabaseId: string): string[] => {
    if (!linkedDatabaseId || !noteIds || noteIds.length === 0) return noteIds;

    const targetDataSource = getDataSource(linkedDatabaseId);
    if (!targetDataSource) {
      // Confirmed deleted → every id it held is dead. Without this the branch
      // below would treat them as "not loaded yet" and keep them all.
      if (isDatasourceDead(linkedDatabaseId)) return [];
      // The target data source hasn't been fetched yet, so we don't know if these IDs are dead.
      // Return them all so they don't incorrectly disappear on initial load.
      return noteIds;
    }

    // Target data source is fetched. We can safely filter out any IDs that aren't in its block list.
    const linkedNotes = getNotesByDataSourceId(linkedDatabaseId);

    // Create a Set for fast lookup of valid IDs
    const validIdsSet = new Set(linkedNotes?.map(n => String(n._id)) || []);

    return noteIds.filter(id => validIdsSet.has(id));
  };

  const contextValue = useMemo(
    () => ({
      // removeBoard, // Commented out
      dataSources,
      setNotesState,
      updateAllNotes,
      setDataSource,
      setDataSources,
      updateDataSource,
      patchDataSourceProperty,
      getDataSource,
      currentDataSource,
      getCurrentDataSource,
      getCurrentDataSourceProperties,
      setCurrentDataSource,
      groupBy,
      setGroupBy,
      getGroupBy,
      filters,
      setBoardFilters,
      getFilters,
      advancedFilters,
      setAdvancedFilters,
      getAdvancedFilters,
      propertyOrder,
      setPropertyOrder,
      propertyVisibility,
      setPropertyVisibility,
      getPropertyVisibility,
      currentBoardNoteId,
      setCurrentBoardNoteId,
      updateNoteComments,
      refreshNotes,
      getNotesByDataSourceId,
      getNoteById,
      getRelationNoteTitle,
      getValidRelationIds,
      currentView,
      setCurrentView,
      sortBy,
      setBoardSortBy,
      getSortBy,
      chartSettings,
      setChartSettings,
      getChartSettings,
      layoutSettings,
      setLayoutSettings,
      getLayoutSettings,
      searchQuery,
      setSearchQuery,
      isHistoryMode,
      setIsHistoryMode,
    }),
    [dataSources, currentDataSource, groupBy, filters, advancedFilters, propertyOrder, propertyVisibility, currentBoardNoteId, currentView, sortBy, chartSettings, layoutSettings, searchQuery, isHistoryMode],
  );

  return <BoardContext.Provider value={contextValue}>{children}</BoardContext.Provider>;
};

/**
 * Session key for property ordering — one bucket per datasource, matching the
 * server. Widget scope resolves to the widget's datasource; standalone to the
 * board's current datasource (board id only until that resolves).
 */
export function usePropertyOrderKey(boardId: string): string {
  const scope = useViewScope();
  const context = useContext(BoardContext);
  if (scope?.databaseSourceId) return String(scope.databaseSourceId);
  return context?.currentDataSource[boardId] ?? boardId;
}

export const useBoard = (): BoardContextType => {
  const context = useContext(BoardContext);
  if (!context) {
    throw new Error("useBoard must be used inside a BoardProvider");
  }
  // When called inside a <ViewScopeProvider> (dashboard widget tree), wrap the
  // helper accessors so every getFilters(boardId)/getSortBy(boardId)/etc. call
  // automatically targets the widget's _id and datasource — even from deeply
  // nested components/hooks that never received an explicitView prop. In
  // standalone mode (no provider), `scope` is null and the original context
  // is returned unchanged — zero impact on existing call sites.
  const scope = useViewScope();
  const widgetViewId = scope?._id ? String(scope._id) : null;
  const widgetDsId = scope?.databaseSourceId ? String(scope.databaseSourceId) : null;

  // Memoize the wrap so the returned context object + its functions are stable
  // across renders (only invalidate when base context, viewId, or dsId
  // changes). Without this, every render hands out fresh function references,
  // which busts downstream useMemo/useEffect deps.
  return useMemo(() => {
    if (!widgetViewId && !widgetDsId) return context;
    return {
      ...context,
      // View-scoped settings: short-circuit to the widget's id.
      getFilters: widgetViewId
        ? (boardId: string, explicitViewTypeId?: string) =>
            context.getFilters(boardId, explicitViewTypeId ?? widgetViewId)
        : context.getFilters,
      getAdvancedFilters: widgetViewId
        ? (boardId: string, explicitViewTypeId?: string) =>
            context.getAdvancedFilters(boardId, explicitViewTypeId ?? widgetViewId)
        : context.getAdvancedFilters,
      getSortBy: widgetViewId
        ? (_boardId: string) => context.sortBy[widgetViewId] || []
        : context.getSortBy,
      getGroupBy: widgetViewId
        ? (_boardId: string) => context.groupBy[widgetViewId]
        : context.getGroupBy,
      getPropertyVisibility: widgetViewId
        ? (_boardId: string) => context.propertyVisibility[widgetViewId] || []
        : context.getPropertyVisibility,
      getLayoutSettings: widgetViewId
        ? (_boardId: string) => context.layoutSettings[widgetViewId]
        : context.getLayoutSettings,
      getChartSettings: widgetViewId
        ? (_boardId: string) => context.chartSettings?.[widgetViewId]
        : context.getChartSettings,
      // Datasource-scoped: short-circuit to the widget's databaseSourceId.
      getCurrentDataSource: widgetDsId
        ? (_boardId: string) => context.dataSources[widgetDsId]
        : context.getCurrentDataSource,
      getCurrentDataSourceProperties: widgetDsId
        ? (_boardId: string) => context.dataSources[widgetDsId]?.properties || {}
        : context.getCurrentDataSourceProperties,
    };
  }, [context, widgetViewId, widgetDsId]);
};
